import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { EnfafExpansionPanelLightComponent } from '../../../../../components/enfaf-expansion-panel-light/enfaf-expansion-panel-light.component';
import { EnfafIconComponent } from '../../../../../components/enfaf-icon/enfaf-icon.component';
import { EnfafImgDraggerComponent } from '../../../../../components/enfaf-img-dragger/enfaf-img-dragger.component';
import { EnfafInputComponent } from '../../../../../components/enfaf-input/enfaf-input.component';
import { EnfafModalComponent } from '../../../../../components/enfaf-modal/enfaf-modal.component';
import { EnfafSelectLightComponent } from '../../../../../components/enfaf-select-light/enfaf-select-light.component';
import { MultiAutocompleteLightComponent, SelectOption } from '../../../../../components/multi-autocomplete-light/multi-autocomplete-light.component';

import { AlertService } from '../../../../../core/services/alert/alert.service';
import { ApiService } from '../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../core/services/toast/toast.service';

import { AmbassadorPopupDto } from '../../../../../models/dto/ambassador-popup.dto';
import { AmbassadorPopupAudience } from '../../../../../models/enums/ambassador-popup/ambassador-popup-audience.enum';
import { AmbassadorPopupTrigger } from '../../../../../models/enums/ambassador-popup/ambassador-popup-trigger.enum';
import { Course } from '../../../../../models/interfaces/course.interface';
import { ComboStudent } from '../../../../../models/interfaces/student.interface';

interface ModalData {
  popup?: AmbassadorPopupDto;
}

@Component({
  selector: 'app-modal-edit-ambassador-popup',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    EnfafModalComponent,
    EnfafInputComponent,
    EnfafSelectLightComponent,
    EnfafExpansionPanelLightComponent,
    EnfafImgDraggerComponent,
    MultiAutocompleteLightComponent,
    EnfafIconComponent
  ],
  templateUrl: './modal-edit-ambassador-popup.component.html',
  styleUrl: './modal-edit-ambassador-popup.component.scss'
})
export class ModalEditAmbassadorPopupComponent implements OnInit, OnDestroy {
  @ViewChild('coursesAutocomplete') coursesAutocomplete?: MultiAutocompleteLightComponent;
  @ViewChild('usersAutocomplete') usersAutocomplete?: MultiAutocompleteLightComponent;

  popupForm!: FormGroup;
  editMode = false;
  isSaving = false;
  uploadedImageFile: File | null = null;

  // Enums para el template
  AmbassadorPopupTrigger = AmbassadorPopupTrigger;
  AmbassadorPopupAudience = AmbassadorPopupAudience;

  // Options para los selects
  triggerOptions = [
    { value: AmbassadorPopupTrigger.ON_FIRST_LOGIN, viewValue: 'En el primer inicio de sesión' },
    { value: AmbassadorPopupTrigger.BY_STUDENT_SENIORITY, viewValue: 'Por antigüedad del alumno' },
    { value: AmbassadorPopupTrigger.AFTER_MULTIPLE_COURSES, viewValue: 'Después de finalizar varios cursos' }
  ];

  audienceOptions = [
    { value: AmbassadorPopupAudience.ALL_STUDENTS, viewValue: 'Todos los alumnos' },
    { value: AmbassadorPopupAudience.STUDENTS_IN_COURSES, viewValue: 'Alumnos de cursos específicos' },
    { value: AmbassadorPopupAudience.SPECIFIC_STUDENTS, viewValue: 'Alumnos específicos' }
  ];

  // Selected items para multi-autocomplete
  selectedCourses: SelectOption[] = [];
  selectedUsers: SelectOption[] = [];

  private destroy$ = new Subject<void>();
  private isComponentDestroyed = false;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ModalEditAmbassadorPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ModalData,
    private apiService: ApiService,
    private toastService: ToastService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.editMode = !!this.data?.popup;
    this.initForm();
    this.setupFormListeners();
  }

  ngOnDestroy(): void {
    this.isComponentDestroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    const popup = this.data?.popup;

    const popupTrigger = popup?.popupTrigger || AmbassadorPopupTrigger.ON_FIRST_LOGIN;

    this.popupForm = this.fb.group({
      description: [popup?.description || '', [Validators.required, Validators.maxLength(500)]],
      link: [popup?.link || '', [Validators.required, Validators.pattern(/^https?:\/\/.+/)]],
      popupTrigger: [popupTrigger, Validators.required],
      triggerThreshold: [
        popup?.triggerThreshold || null,
        popupTrigger === AmbassadorPopupTrigger.ON_FIRST_LOGIN ? [] : [Validators.required, Validators.min(1)]
      ],
      popupAudience: [popup?.popupAudience || AmbassadorPopupAudience.ALL_STUDENTS, Validators.required],
      popupImageUrl: [popup?.popupImageUrl || '']
    });

    // Set selected courses and users if editing
    if (this.editMode && popup) {
      if (popup.courses && popup.courses.length > 0) {
        this.selectedCourses = popup.courses.map((course) => ({
          id: course.courseId,
          title: course.name,
          value: course.courseId
        }));
      }
      if (popup.users && popup.users.length > 0) {
        this.selectedUsers = popup.users.map((user) => ({
          id: user.userId,
          title: `${user.name} ${user.lastname}`.trim(),
          value: user.userId
        }));
      }
    }
  }

  private setupFormListeners(): void {
    // Show/hide triggerThreshold based on popupTrigger
    this.popupForm
      .get('popupTrigger')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((trigger) => {
        const thresholdControl = this.popupForm.get('triggerThreshold');
        if (trigger === AmbassadorPopupTrigger.BY_STUDENT_SENIORITY) {
          thresholdControl?.setValidators([Validators.required, Validators.min(1)]);
          thresholdControl?.setValue(15);
        } else if (trigger === AmbassadorPopupTrigger.AFTER_MULTIPLE_COURSES) {
          thresholdControl?.setValidators([Validators.required, Validators.min(1)]);
          thresholdControl?.setValue(3);
        } else {
          thresholdControl?.clearValidators();
          thresholdControl?.setValue(null);
        }
        thresholdControl?.updateValueAndValidity();
      });

    // Reset autocomplete selections when audience changes
    this.popupForm
      .get('popupAudience')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.coursesAutocomplete) {
          this.coursesAutocomplete.resetSelection();
        }
        if (this.usersAutocomplete) {
          this.usersAutocomplete.resetSelection();
        }
        this.selectedCourses = [];
        this.selectedUsers = [];
      });
  }

  fetchCourses = (search: string, page: number, pageSize: number): Observable<{ data: SelectOption[]; total: number }> => {
    return new Observable((observer) => {
      const params: any = {
        page: page,
        pageSize: pageSize
      };

      if (search && search.trim()) {
        params.search = search.trim();
      }

      firstValueFrom(this.apiService.fetchAllCourses(false, params))
        .then((response) => {
          const courses = response.courses || response.data || [];
          const mappedData: SelectOption[] = courses.map((course: Course) => ({
            id: course.courseId,
            title: course.name,
            value: course.courseId
          }));

          observer.next({
            data: mappedData,
            total: response.pagination?.totalItems || response.pagination?.total || courses.length
          });
          observer.complete();
        })
        .catch((error) => {
          if (this.isComponentDestroyed) {
            observer.complete();
            return;
          }
          console.error('Error fetching courses:', error);
          observer.error(error);
        });
    });
  };

  fetchUsers = (search: string, page: number, pageSize: number): Observable<{ data: SelectOption[]; total: number }> => {
    return new Observable((observer) => {
      const params: any = {
        page: page + 1,
        limit: pageSize
      };

      if (search && search.trim()) {
        params.search = search.trim();
      }

      firstValueFrom(this.apiService.fetchReducedStudents(params))
        .then((response: any) => {
          const mappedData: SelectOption[] = response.data.map((user: ComboStudent) => ({
            id: user.id,
            title: `${user.name} ${user.lastname} (${user.email})`,
            value: user.id
          }));

          observer.next({
            data: mappedData,
            total: response.pagination?.total || response.pagination?.totalItems || response.total || response.data.length
          });
          observer.complete();
        })
        .catch((error) => {
          console.error('Error fetching users:', error);
          observer.error(error);
        });
    });
  };

  // Selection change handlers
  onCoursesSelectionChange(selected: SelectOption[]): void {
    this.selectedCourses = selected;
  }

  onUsersSelectionChange(selected: SelectOption[]): void {
    this.selectedUsers = selected;
  }

  // Image upload handlers
  uploadImage(event: { imageToUpload: File | null; imageToUploadSrc: string | null }): void {
    if (event.imageToUpload) {
      this.uploadedImageFile = event.imageToUpload;
      if (event.imageToUploadSrc) {
        this.popupForm.patchValue({ popupImageUrl: event.imageToUploadSrc });
      }
    }
  }

  deleteImage(): void {
    this.uploadedImageFile = null;
    this.popupForm.patchValue({ popupImageUrl: '' });
  }

  // Remove selected items
  removeCourse(course: SelectOption): void {
    if (this.coursesAutocomplete) {
      this.coursesAutocomplete.removeSelected(course);
      this.selectedCourses = this.coursesAutocomplete.selected;
    }
  }

  removeUser(user: SelectOption): void {
    if (this.usersAutocomplete) {
      this.usersAutocomplete.removeSelected(user);
      this.selectedUsers = this.usersAutocomplete.selected;
    }
  }

  // Get label for triggerThreshold based on popupTrigger
  get triggerThresholdLabel(): string {
    const trigger = this.popupForm.get('popupTrigger')?.value;
    if (trigger === AmbassadorPopupTrigger.BY_STUDENT_SENIORITY) {
      return 'Días de antigüedad del alumno';
    } else if (trigger === AmbassadorPopupTrigger.AFTER_MULTIPLE_COURSES) {
      return 'Cursos finalizados';
    }
    return '';
  }

  get showTriggerThreshold(): boolean {
    const trigger = this.popupForm.get('popupTrigger')?.value;
    return trigger === AmbassadorPopupTrigger.BY_STUDENT_SENIORITY || trigger === AmbassadorPopupTrigger.AFTER_MULTIPLE_COURSES;
  }

  get showCoursesSelection(): boolean {
    return this.popupForm.get('popupAudience')?.value === AmbassadorPopupAudience.STUDENTS_IN_COURSES;
  }

  get showUsersSelection(): boolean {
    return this.popupForm.get('popupAudience')?.value === AmbassadorPopupAudience.SPECIFIC_STUDENTS;
  }

  async savePopup(): Promise<void> {
    if (this.popupForm.invalid) {
      this.toastService.presentErrorToast('Por favor, completa todos los campos requeridos');
      return;
    }

    // Validate audience-specific selections
    if (this.showCoursesSelection && this.selectedCourses.length === 0) {
      this.toastService.presentErrorToast('Debes seleccionar al menos un curso');
      return;
    }

    if (this.showUsersSelection && this.selectedUsers.length === 0) {
      this.toastService.presentErrorToast('Debes seleccionar al menos un usuario');
      return;
    }

    this.isSaving = true;

    try {
      const formValue = this.popupForm.value;
      const formData = new FormData();

      // Prepare popup data DTO
      const popupData: any = {
        description: formValue.description,
        link: formValue.link,
        popupTrigger: formValue.popupTrigger,
        triggerThreshold: formValue.triggerThreshold || undefined,
        popupAudience: formValue.popupAudience,
        courseIds: this.showCoursesSelection ? this.selectedCourses.map((c) => c.id) : undefined,
        userIds: this.showUsersSelection ? this.selectedUsers.map((u) => u.id) : undefined,
        popupImageUrl: formValue.popupImageUrl || undefined
      };

      // Add image if exists
      if (this.uploadedImageFile) {
        formData.append('image', this.uploadedImageFile, this.uploadedImageFile.name);
      }

      formData.append('form', JSON.stringify(popupData));

      // Add form data
      if (this.editMode && this.data?.popup) {
        await firstValueFrom(this.apiService.updateAmbassadorPopup(this.data.popup.id, formData));
        this.toastService.presentSuccessToast('Popup actualizado correctamente');
      } else {
        // Create new popup
        await firstValueFrom(this.apiService.createAmbassadorPopup(formData));
        this.toastService.presentSuccessToast('Popup creado correctamente');
      }

      this.dialogRef.close(true);
    } catch (error) {
      console.error('Error saving popup:', error);
      this.toastService.presentErrorToast('Error al guardar el popup');
    } finally {
      this.isSaving = false;
    }
  }

  async confirmCloseModal(): Promise<void> {
    if (this.popupForm.dirty && !this.isSaving) {
      const confirmed = await this.alertService.presentConfirmActionAlert(
        'Cerrar sin guardar',
        '¿Estás seguro de que quieres cerrar sin guardar los cambios?',
        'Cancelar',
        'Cerrar',
        'star',
        'Los cambios no guardados se perderán.'
      );

      if (confirmed) {
        this.dialogRef.close(false);
      }
    } else {
      this.dialogRef.close(false);
    }
  }
}
